import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, FileSignature, Clock, Eye, PenLine, CheckCircle2, ExternalLink, ListChecks } from "lucide-react";
import { AppShell } from "@/components/app/AppShell";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { formatDate } from "@/lib/format";

const STAGE = {
  queued: { label: "Waiting turn", cls: "border-slate-200 bg-slate-50 text-slate-500", icon: Clock },
  sent: { label: "Sent", cls: "border-slate-200 bg-slate-50 text-slate-600", icon: Clock },
  viewed: { label: "Viewed", cls: "border-amber-200 bg-amber-50 text-amber-700", icon: Eye },
  awaiting_countersign: { label: "Awaiting your countersign", cls: "border-amber-200 bg-amber-50 text-amber-800", icon: PenLine },
  signed: { label: "Signed", cls: "border-emerald-200 bg-emerald-50 text-emerald-700", icon: CheckCircle2 },
  countersigned: { label: "Completed", cls: "border-emerald-200 bg-emerald-50 text-emerald-700", icon: CheckCircle2 },
};

const StatCard = ({ label, value, tone }) => (
  <Card className={`rounded-xl border p-4 ${tone}`}>
    <div className="text-2xl font-semibold text-foreground">{value}</div>
    <div className="text-xs text-muted-foreground">{label}</div>
  </Card>
);

export default function SignerDashboard() {
  const { company } = useAuth();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("pending");

  const load = async () => {
    if (!company) return;
    try { const res = await api.get(`/companies/${company.id}/signatures`); setData(res.data); }
    catch (e) { console.error("signatures load failed", e); setData({ signatures: [], counts: {}, pending: 0, completed: 0, total: 0 }); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [company]);

  const pendingStatuses = ["queued", "sent", "viewed", "awaiting_countersign"];
  const rows = (data?.signatures || []).filter((s) =>
    tab === "pending" ? pendingStatuses.includes(s.status) : ["signed", "countersigned"].includes(s.status));

  return (
    <AppShell title="Signatures" subtitle="Every signature request across your documents, and exactly where each one stands">
      {!data ? (
        <div className="flex justify-center py-20 text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : (
        <div className="space-y-6" data-testid="signer-dashboard">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <StatCard label="Total requests" value={data.total} tone="bg-card" />
            <StatCard label="Pending" value={data.pending} tone="bg-amber-50/50" />
            <StatCard label="Awaiting your countersign" value={data.counts?.awaiting_countersign || 0} tone="bg-amber-50" />
            <StatCard label="Completed" value={data.completed} tone="bg-emerald-50/50" />
          </div>

          <div className="flex items-center gap-2">
            <Button variant={tab === "pending" ? "default" : "outline"} size="sm" onClick={() => setTab("pending")} data-testid="tab-pending">Pending ({data.pending})</Button>
            <Button variant={tab === "completed" ? "default" : "outline"} size="sm" onClick={() => setTab("completed")} data-testid="tab-completed">Completed ({data.completed})</Button>
          </div>

          <Card className="rounded-xl border bg-card">
            {rows.length === 0 ? (
              <div className="flex flex-col items-center gap-2 py-16 text-center text-muted-foreground">
                <ListChecks className="h-8 w-8" />
                <p className="text-sm">No {tab} signature requests.</p>
              </div>
            ) : (
              <div className="divide-y">
                {rows.map((s) => {
                  const st = STAGE[s.status] || STAGE.sent;
                  const Icon = st.icon;
                  return (
                    <div key={s.id} className="flex items-center justify-between gap-3 p-4" data-testid={`sig-row-${s.id}`}>
                      <div className="flex min-w-0 items-center gap-3">
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary"><FileSignature className="h-4 w-4" /></div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5">
                            <span className="truncate text-sm font-medium text-foreground">{s.title || "Document"}</span>
                            {s.envelope_id && <Badge variant="secondary" className="shrink-0 px-1.5 py-0 text-[10px] font-normal">Signer {(s.order ?? 0) + 1} of {s.total_signers}</Badge>}
                          </div>
                          <div className="truncate text-[11px] text-muted-foreground">
                            {s.recipient_name} · {s.recipient_email} · sent {formatDate(s.sent_at)}
                            {s.signed_at ? ` · signed ${formatDate(s.signed_at)}` : ""}
                          </div>
                        </div>
                      </div>
                      <div className="flex shrink-0 items-center gap-2">
                        <Badge variant="outline" className={`gap-1 ${st.cls}`}><Icon className="h-3 w-3" /> {st.label}</Badge>
                        {s.document_id && (
                          <Link to={`/app/document-builder/${s.document_id}`}>
                            <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`sig-open-${s.id}`}><ExternalLink className="h-3.5 w-3.5" /></Button>
                          </Link>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        </div>
      )}
    </AppShell>
  );
}
